export interface PipelineSummaryItem {
  name: string;
  deal_count: number;
  loan_amount_gbp: number;
  average_deal_size_gbp?: number;
}

const normalizeKey = (name: string) => name.trim().toLowerCase();

const STATUS_TO_CONDENSED: Record<string, string> = {
  closed: "Closed",
  "closed - apollo": "Closed - Apollo",
  "preview memo": "In Closing",
  "ic memo": "In Closing",
  "signed / committed": "In Closing",
  "tenfifty large loan": "Referred to Apollo",
  screening: "In Screening",
  "term sheet submitted": "In Screening",
  "received materials": "In Screening",
  "on hold": "In Screening",
  "apollo reviewing": "In Screening",
  killed: "Killed",
  "lost to other lender": "Lost to Other Lenders",
  "lost to other lenders": "Lost to Other Lenders",
  "referred to apollo": "Referred to Apollo",
};

const CONDENSED_STATUS_ORDER = [
  "Closed",
  "Closed - Apollo",
  "In Closing",
  "In Screening",
  "Killed",
  "Lost to Other Lenders",
  "Referred to Apollo",
];

const SECTOR_TO_CONDENSED: Record<string, string> = {
  hotel: "Hospitality",
  leisure: "Hospitality",
  "hotel & leisure": "Hospitality",
  hospitality: "Hospitality",
  retail: "Retail",
  "retail warehouse": "Retail",
  "retail & retail warehouse": "Retail",
  residential: "Living",
  pbsa: "Living",
  "senior housing": "Living",
  "residential & pbsa & senior housing": "Living",
  living: "Living",
  "care home": "Living",
  automotive: "Infrastructure",
  infrastructure: "Infrastructure",
};

function mapCondensedName(
  name: string,
  lookup: Record<string, string>,
): string {
  return lookup[normalizeKey(name)] ?? name;
}

function mergeSummaryItems(
  items: PipelineSummaryItem[],
  mapName: (name: string) => string,
  preferredOrder?: string[],
): PipelineSummaryItem[] {
  const merged = new Map<
    string,
    { deal_count: number; loan_amount_gbp: number }
  >();

  for (const item of items) {
    const key = mapName(item.name);
    const prev = merged.get(key) ?? { deal_count: 0, loan_amount_gbp: 0 };
    prev.deal_count += item.deal_count;
    prev.loan_amount_gbp += item.loan_amount_gbp;
    merged.set(key, prev);
  }

  const toRow = (
    name: string,
    agg: { deal_count: number; loan_amount_gbp: number },
  ): PipelineSummaryItem => ({
    name,
    deal_count: agg.deal_count,
    loan_amount_gbp: agg.loan_amount_gbp,
    average_deal_size_gbp:
      agg.deal_count > 0 ? agg.loan_amount_gbp / agg.deal_count : 0,
  });

  const result: PipelineSummaryItem[] = [];
  const used = new Set<string>();

  if (preferredOrder) {
    for (const name of preferredOrder) {
      const agg = merged.get(name);
      if (agg && (agg.deal_count > 0 || agg.loan_amount_gbp > 0)) {
        result.push(toRow(name, agg));
        used.add(name);
      }
    }
  }

  const remaining = [...merged.entries()]
    .filter(([name]) => !used.has(name))
    .map(([name, agg]) => toRow(name, agg))
    .filter((row) => row.deal_count > 0 || row.loan_amount_gbp > 0);

  remaining.sort((a, b) => b.loan_amount_gbp - a.loan_amount_gbp);
  result.push(...remaining);

  return result;
}

export function condenseStatusSummaries(
  items: PipelineSummaryItem[],
): PipelineSummaryItem[] {
  return mergeSummaryItems(
    items,
    (name) => mapCondensedName(name, STATUS_TO_CONDENSED),
    CONDENSED_STATUS_ORDER,
  );
}

export function condenseSectorSummaries(
  items: PipelineSummaryItem[],
): PipelineSummaryItem[] {
  return mergeSummaryItems(
    items,
    (name) => mapCondensedName(name, SECTOR_TO_CONDENSED),
  );
}
